/**
 * inventory-create/render — 재고 등록 페이지의 그리기(순수). 탭 머리 · 사진 상자 · ① 카탈로그 · ② 실물.
 * ③ 구성은 ServerConfigEditor 컴포넌트가 그린다. 사이트 공통 꼴을 쓴다 — form-card · form-grid · form-field · kv-row · chip · btn-*. 이 페이지만의 CSS 는 `ic-`.
 * 화면 말(현민 2026-10-02): 모델명(코드 model) · SKU · 모델 상세 · SKU 상세 · 등록된 구성(코드 product) · 실물(unit — 2026-10-01 판정 그대로). 코드 말은 그대로.
 */
import { assert } from '../../lib/assert';
import { escapeHtml } from '../../lib/escape-html';
import type { BulletinSpec, CatalogMatch, ChassisBayView, ChassisDetail, ChassisModelDetail, ChassisSpec, HcltProductView, ModelDocView, PreconfigView } from '../../api/client';
import { REGISTER_STEPS, type PhotoLine, type RegisterStep, type SaveResult } from './types';
import type { CatalogStep, InspectAnswer, InspectCandidate, InspectCandidateGroup, InventoryCreateTab, LabelNumberFrame, PnSource, ScanLine, ScanList, ScanTarget, ScanType, ServerSection } from './types';
import { DEFAULT_FRAME, LABEL_NUMBER_FRAMES, SCAN_BRANDS, SCAN_TYPES } from './types';

// ─── 공통 조각 ───
const EMPTY = '<span class="text-muted">—</span>';

const photoMark = (s: ServerSection, field: string): string => (s.fromPhoto.has(field) ? '<span class="ic-from-photo" title="사진에서 읽음">📷</span>' : '');

/** 값 하나를 글로. null 은 —, 참/거짓은 예/아니요, 숫자엔 단위 */
function text(value: string | number | boolean | null, unit = ''): string {
  if (value === null || value === '') return EMPTY;
  if (typeof value === 'boolean') return value ? '예' : '아니요';
  return `${escapeHtml(String(value))}${unit}`;
}

/** kv 줄 하나 — 사이트 공통 kv-row */
const kv = (key: string, value: string): string => `<div class="kv-row"><span class="kv-key">${key}</span><span class="kv-val">${value}</span></div>`;

/** kv 묶음 — 작은 머리글 + 줄들 */
const kvGroup = (title: string, rows: string): string => `<div class="ic-kv-group"><h4>${title}</h4>${rows}</div>`;

/** 등록 꼴 고르기 — 파트 한 장 · 서버 한 대 · 일괄. 메뉴 탭 줄(구매 목록 · 재고 등록) 아래 둘째 줄 */
export function renderTabs(tab: InventoryCreateTab): string {
  const t = (key: InventoryCreateTab, label: string): string =>
    `<a class="ic-tab${key === tab ? ' active' : ''}" data-link href="/inventory/new/${key}">${label}</a>`;
  return `<nav class="ic-tabs ic-mode">${t('part', '파트 한 장')}${t('server', '서버 한 대')}<a class="btn-ghost ic-bulk" data-link href="/inventory/stocktake">이카운트 자료 올리기(실사 탭) →</a></nav>`;
}

// ─── 스캔 목록 (2026-10-06) — 버블(실물) · 카드(자리) · 틀(라벨 번호 순서) ───

const SCAN_TYPE_WORD: Record<ScanType, string> = { serial: '시리얼', security_id: 'SID', tracking_number: 'CT', service_tag: 'Service Tag', ppid: 'PPID', wwn: 'WWN' };
const PN_SOURCE_WORD: Record<PnSource, string> = { purchase: '구매', photo: '사진', manual: '수기' };

/** 카드의 라벨 번호 틀 — (카테고리, 브랜드) 가 맞는 것, 없으면 브랜드 무관인 것, 그것도 없으면 시리얼 하나. 풀(카드 없음)은 버블 = 바코드 하나 */
export function frameOf(target: ScanTarget | null): LabelNumberFrame {
  if (target === null || target.category === null) return DEFAULT_FRAME;
  const exact = LABEL_NUMBER_FRAMES.find((frame) => frame.category === target.category && frame.brand === target.brand);
  const loose = LABEL_NUMBER_FRAMES.find((frame) => frame.category === target.category && frame.brand === null);
  return exact ?? loose ?? DEFAULT_FRAME;
}

/** 틀에서 아직 안 찍힌 다음 자리 — 없으면 null(자리가 다 찼다) */
export function nextFrameTypeOf(line: ScanLine, frame: LabelNumberFrame): ScanType | null {
  return frame.types.find((type) => line.numbers[type] === undefined) ?? null;
}

/** 필수 자리 중 빈 것 — 저장을 막는 재료 */
export function missingRequiredOf(line: ScanLine, frame: LabelNumberFrame): ScanType[] {
  return frame.types.slice(0, frame.required).filter((type) => line.numbers[type] === undefined);
}

/** 버블의 머리 번호 — 시리얼이 있으면 그것, 없으면 찍힌 첫 번호 */
export function scanHeadOf(line: ScanLine): string {
  const serial = line.numbers.serial;
  if (serial !== undefined) return serial;
  const first = SCAN_TYPES.map((type) => line.numbers[type]).find((value) => value !== undefined);
  return first ?? '';
}

export function targetOf(scan: ScanList, key: string | null): ScanTarget | null {
  return key === null ? null : scan.targets.find((target) => target.key === key) ?? null;
}

export function linesOfTarget(scan: ScanList, key: string): number[] {
  return scan.lines.flatMap((line, index) => (line.target === key ? [index] : []));
}

/** 버블 하나 — 틀의 자리 순서대로 칩(찍힌 번호, 끌어서 자리 바꿈) 또는 빈 자리(놓는 곳). 필수 자리가 비면 빨갛게. 열린(번호 더 받는) 버블엔 확정 버튼.
 *  풀에선 ✕(지움), 카드 안에선 ↩(풀로). 고른 것은 is-selected */
function renderBubble(scan: ScanList, index: number, place: 'pool' | 'card'): string {
  const line = scan.lines[index];
  assert(line !== undefined, `스캔 버블 ${index} 가 없다`);
  const frame = frameOf(targetOf(scan, line.target));
  const bIsSelected = scan.selected.has(index);
  const bIsOpen = scan.open === index;
  const slots = frame.types.map((type, position) => {
    const value = line.numbers[type];
    const bIsRequired = position < frame.required;
    if (value === undefined) return `<span class="ic-slot${bIsRequired && !bIsOpen ? ' is-missing' : ''}" data-action="slot" data-index="${index}" data-type="${type}" title="여기에 칩을 놓으면 ${SCAN_TYPE_WORD[type]} 가 된다">${position + 1} ${SCAN_TYPE_WORD[type]}${bIsRequired ? '' : '·선택'}</span>`;
    return `<span class="ic-chip" draggable="true" data-action="chip" data-index="${index}" data-type="${type}" title="끌어서 자리를 바꾼다"><small>${position + 1} ${SCAN_TYPE_WORD[type]}</small><b class="mono">${escapeHtml(value)}</b></span>`;
  }).join('');
  const button = place === 'pool'
    ? `<button type="button" class="sce-mini" data-action="bubble-remove" data-index="${index}" title="이 실물을 지운다">✕</button>`
    : `<button type="button" class="sce-mini" data-action="bubble-out" data-index="${index}" title="풀로 뺀다">↩</button>`;
  const close = bIsOpen ? `<button type="button" class="sce-mini ic-bubble-close" data-action="bubble-close" data-index="${index}" title="이 실물 끝 — 빈 엔터와 같다">확정</button>` : '';
  return `<div class="ic-bubble${bIsSelected ? ' is-selected' : ''}${bIsOpen ? ' is-open' : ''}" draggable="true" data-action="bubble" data-index="${index}" title="누르면 고름 · 끌어서 카드에">
    <span class="ic-bubble-brand">${line.brand}</span>${slots}${close}${button}
  </div>`;
}

/** 카드 하나 — PN · 설명 · 출처 · 자리(qty 만큼 칸, 찬 칸엔 버블). 활성 카드엔 "다음: SID" 힌트. 누르면 활성(고른 버블이 있으면 그것들을 넣음) */
function renderTarget(scan: ScanList, target: ScanTarget): string {
  const indexes = linesOfTarget(scan, target.key);
  const bIsActive = scan.activeTarget === target.key;
  const filled = indexes.length;
  const emptyCount = target.qty === null ? 0 : Math.max(0, target.qty - filled);
  const bIsOver = target.qty !== null && filled > target.qty;
  const frame = frameOf(target);
  const openLine = scan.open === null ? undefined : scan.lines[scan.open];
  const next = openLine !== undefined && openLine.target === target.key ? nextFrameTypeOf(openLine, frame) : null;   // 열린 버블이 이 카드에 있으면 활성이 아니어도 힌트
  const order = frame.types.map((type, position) => `${position + 1} ${SCAN_TYPE_WORD[type]}${position < frame.required ? '' : '(선택)'}`).join(' → ');
  const hint = next !== null ? `다음 자리: <b>${SCAN_TYPE_WORD[next]}</b> — 없으면 빈 엔터 · 확정` : !bIsActive ? '' : `찍으면 새 실물 — 자리 ${order}`;
  return `<div class="ic-target${bIsActive ? ' is-active' : ''}${bIsOver ? ' is-over' : ''}" data-action="target" data-key="${escapeHtml(target.key)}" title="누르면 이 카드에 찍기 · 고른 버블이 있으면 여기 넣기">
    <div class="ic-target-head">
      <span class="mono"><b>${escapeHtml(target.option_part)}</b></span>
      <span class="ic-target-desc">${escapeHtml(target.description)}</span>
      <span class="chip ${target.source === 'purchase' ? 'chip-success' : target.source === 'photo' ? 'chip-primary' : 'chip-muted'}">${PN_SOURCE_WORD[target.source]}</span>
      <span class="ic-target-count${bIsOver ? ' pv-bad' : ''}">${filled}${target.qty === null ? '' : ` / ${target.qty}`}</span>
      <button type="button" class="sce-mini" data-action="target-remove" data-key="${escapeHtml(target.key)}" title="카드를 뺀다(버블은 풀로)">✕</button>
    </div>
    <div class="ic-target-slots">
      ${indexes.map((index) => renderBubble(scan, index, 'card')).join('')}
      ${'<div class="ic-slot-empty">빈 칸</div>'.repeat(emptyCount)}
      ${target.qty === null ? '<div class="ic-slot-empty is-open">+</div>' : ''}
    </div>
    ${hint === '' ? '' : `<div class="ic-target-hint">${hint}</div>`}
  </div>`;
}

/** 스캔 카드 — 브랜드 셀렉터 + 스캔 칸, 왼쪽 풀(카드 밖 버블) · 오른쪽 카드들(+ PN 직접). 활성 카드가 있으면 찍는 것이 바로 거기로 */
export function renderScan(s: ServerSection): string {
  const { scan } = s;
  const pool = scan.lines.flatMap((line, index) => (line.target === null ? [index] : []));
  const emptySlots = scan.targets.reduce((sum, target) => sum + (target.qty === null ? 0 : Math.max(0, target.qty - linesOfTarget(scan, target.key).length)), 0);
  const active = targetOf(scan, scan.activeTarget);
  const openLine = scan.open === null ? undefined : scan.lines[scan.open];
  const openNext = openLine === undefined ? null : nextFrameTypeOf(openLine, frameOf(targetOf(scan, openLine.target)));
  const missing = scan.lines.filter((line, index) => index !== scan.open && missingRequiredOf(line, frameOf(targetOf(scan, line.target))).length > 0).length;
  const where = openLine !== undefined && openNext !== null
    ? `열린 실물 <b class="mono">${escapeHtml(scanHeadOf(openLine))}</b> 의 <b>${SCAN_TYPE_WORD[openNext]}</b> 자리에 들어갑니다 — 없으면 빈 엔터 · 확정`
    : active === null ? '풀로 갑니다 — 카드를 누르거나 PN 을 찍으면 그 카드로' : `<b class="mono">${escapeHtml(active.option_part)}</b> 카드로 갑니다`;
  return `
    <section class="form-card ic-card ic-scan">
      <h2>스캔 <small>바코드 기기로 한 장에 한 번씩 — 시리얼은 여기가 진실. 버블 수가 곧 실물 수</small></h2>
      <div class="ic-scan-bar">
        <label>브랜드 <select data-action="scan-brand">${SCAN_BRANDS.map((brand) => `<option ${brand === scan.brand ? 'selected' : ''}>${brand}</option>`).join('')}</select></label>
        <input type="text" class="ic-scan-input mono" data-action="scan-input" placeholder="찍으면 여기 들어옵니다 (빈 엔터 = 이 실물 끝)" autocomplete="off">
        <button type="button" class="btn-ghost" data-action="scan-purchase">${scan.isPurchaseOpen ? '구매 품목 닫기' : '구매 품목 카드'}</button>
        <button type="button" class="btn-ghost" data-action="scan-place-all" ${pool.length === 0 || scan.targets.length === 0 ? 'disabled' : ''} title="풀의 버블을 카드에 — 사진 시리얼 끝 4자가 맞는 카드, 나머지는 순서대로 빈 칸에">자동 배치</button>
        <button type="button" class="btn-ghost" data-action="scan-mock" title="시연 — 구매 카드 넷 + 섀시 · SSD · 메모리를 찍은 것처럼">시연 스캔</button>
      </div>
      <div class="text-muted ic-scan-where">찍는 것은 ${where}</div>
      ${scan.isPurchaseOpen ? renderScanPurchaseLines(scan) : ''}
      <div class="ic-scan-body">
        <div class="ic-scan-pool" data-action="pool">
          <div class="ic-read-title">풀 <span class="text-muted">카드 밖 실물 ${pool.length}</span></div>
          ${pool.length === 0 ? '<div class="text-muted ic-scan-empty">비었습니다</div>' : pool.map((index) => renderBubble(scan, index, 'pool')).join('')}
          ${scan.selected.size === 0 ? '' : `<div class="text-muted ic-scan-picked">고른 버블 ${scan.selected.size} — 카드를 누르면 들어갑니다</div>`}
        </div>
        <div class="ic-scan-targets">
          <div class="ic-read-title">카드 <span class="text-muted">자리 ${scan.targets.length} · 빈 칸 ${emptySlots}</span></div>
          ${scan.targets.map((target) => renderTarget(scan, target)).join('')}
          <div class="ic-target-new">
            <input type="text" class="mono" data-action="scan-new-pn" placeholder="PN 직접 — 카드가 없을 때">
            <button type="button" class="btn-ghost" data-action="scan-new-target">카드 추가</button>
          </div>
        </div>
      </div>
      <div class="text-muted ic-scan-foot">실물 ${scan.lines.length} · 카드 ${scan.targets.length} · 빈 칸 ${emptySlots} · 풀 ${pool.length}${missing > 0 ? ` · <span class="pv-bad">필수 번호 빠진 실물 ${missing}</span>` : ''}</div>
    </section>`;
}

/** 목업 — 발주서(PO)의 구매 줄. 실제는 purchase_order_item 에서 온다. 누르면 그 줄이 카드가 된다(출처 구매). 수주 줄은 세팅 · 출고의 것이라 여기 안 온다 */
export const MOCK_PURCHASE_LINES = [
  { po: 'PO-2026-0042', option_part: 'P19766-B21', category: 'chassis', description: 'HPE DL380 Gen10 8SFF CTO Server', qty: 1 },
  { po: 'PO-2026-0042', option_part: 'P02510-B21', category: 'cpu',     description: 'Intel Xeon-Gold 6248R', qty: 2 },
  { po: 'PO-2026-0042', option_part: 'P00924-B21', category: 'memory',  description: 'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit', qty: 12 },
  { po: 'PO-2026-0042', option_part: 'P18434-B21', category: 'ssd',     description: 'HPE 960GB SATA RI SFF SC MV SSD', qty: 4 },
] as const;

function renderScanPurchaseLines(scan: ScanList): string {
  const row = (line: typeof MOCK_PURCHASE_LINES[number], index: number): string => {
    const bIsCard = scan.targets.some((target) => target.option_part === line.option_part);
    return `<button type="button" class="ic-scan-purchase-line" data-action="scan-purchase-line" data-index="${index}" ${bIsCard ? 'disabled' : ''}><span class="mono">${line.option_part}</span> ${escapeHtml(line.description)} <b>×${line.qty}</b>${bIsCard ? ' <span class="chip chip-muted">카드 있음</span>' : ''}</button>`;
  };
  return `<div class="ic-scan-purchase"><div class="ic-read-title">구매 품목 — PO-2026-0042 ㈜서버마트(매입처) <button type="button" class="btn-ghost" data-action="scan-purchase-all">전부 카드로</button></div>${MOCK_PURCHASE_LINES.map(row).join('')}</div>`;
}

/** ⓪ 사진 상자 — 목업: 올리기 누르면 가짜로 읽어 ① ② 를 채운다 */
export function renderPhoto(s: ServerSection): string {
  const { photo } = s;
  const bReading = photo.status === 'reading';
  const panel = photo.status === 'read' ? renderPhotoLines(photo.lines) : photo.status === 'confirmed' ? renderPhotoConfirmed(photo.lines) : '';
  return `
    <section class="form-card ic-card ic-photo">
      <div class="ic-photo-drop${photo.status === 'confirmed' ? ' done' : ''}">
        <strong>📷 라벨 사진</strong> <span class="text-muted">섀시 풀아웃 태그 · 부품 라벨 · 스크린샷 — 여러 장 한 번에. 사진은 <b>PN</b> 을 주고, 스캔 목록의 시리얼과 끝 4자로 맞춰 붙입니다(사진 속 시리얼은 저장 안 함)</span>
        <div class="ic-photo-actions">
          <button type="button" class="${photo.status === 'confirmed' ? 'btn-ghost' : 'btn-primary'}" data-action="photo-mock" ${bReading ? 'disabled' : ''}>${bReading ? '읽는 중…' : photo.status === 'confirmed' ? '사진 더 올리기' : '사진 올리기'}</button>
          ${photo.status === 'idle' ? '<span class="text-muted">사진 없이 손으로 치려면 아래 ①부터</span>' : ''}
        </div>
        ${photo.error ? `<div class="ce-alert">${escapeHtml(photo.error)}</div>` : ''}
      </div>
      ${panel}
    </section>`;
}

/** 읽은 것 — 줄마다 ✕(이 줄은 틀렸다). [확인] 이 칸 · 트레이에 넣는다. 섀시 줄 먼저, 부품 줄 다음 */
function renderPhotoLines(lines: PhotoLine[]): string {
  const row = (line: PhotoLine, n: number): string => {
    const body = line.kind === 'chassis'
      ? `<span class="ic-read-label">${escapeHtml(line.label)}</span><span class="ic-read-value mono">${escapeHtml(line.value)}</span>`
      : `<span class="ic-read-label">${escapeHtml(categoryWord(line.part.category))}</span><span class="ic-read-value"><span class="mono">${escapeHtml(line.part.option_part)}</span> ${escapeHtml(line.part.description)} <b>×${line.qty}</b>${line.part.serial === null ? '' : ` <span class="text-muted">S/N ${escapeHtml(line.part.serial)}</span>`}</span>`;
    return `<div class="ic-read-line${line.keep ? '' : ' dropped'}">${body}<button type="button" class="sce-mini" data-action="photo-line" data-index="${n}" title="${line.keep ? '이 줄은 틀렸다 — 뺀다' : '되살린다'}">${line.keep ? '✕' : '되살리기'}</button></div>`;
  };
  const chassis = lines.map((l, n) => [l, n] as const).filter(([l]) => l.kind === 'chassis');
  const parts   = lines.map((l, n) => [l, n] as const).filter(([l]) => l.kind === 'part');
  const kept = lines.filter((l) => l.keep).length;
  return `
    <div class="ic-read">
      <div class="ic-read-head">읽은 것 <small class="text-muted">사진 3장 · ${lines.length}줄. 틀린 줄은 ✕ — 나머지가 ① ② ③ 에 들어갑니다</small></div>
      <div class="ic-read-cols">
        <div><div class="ic-read-title">섀시 라벨</div>${chassis.map(([l, n]) => row(l, n)).join('')}</div>
        <div><div class="ic-read-title">부품 라벨</div>${parts.map(([l, n]) => row(l, n)).join('')}</div>
      </div>
      <div class="ic-read-actions"><button type="button" class="btn-primary" data-action="photo-confirm">확인 — ${kept}줄을 칸에 넣기</button></div>
    </div>`;
}

function renderPhotoConfirmed(lines: PhotoLine[]): string {
  const parts = lines.filter((l): l is Extract<PhotoLine, { kind: 'part' }> => l.kind === 'part' && l.keep);
  const qty = parts.reduce((a, l) => a + l.qty, 0);
  return `<div class="ic-read ic-read-done text-muted">📷 섀시 라벨 ${lines.filter((l) => l.kind === 'chassis' && l.keep).length}칸 · 부품 ${parts.length}종 ${qty}장을 읽어 넣었습니다 — ① ② 의 📷 칸, ③ 트레이의 「📷 읽은 것」</div>`;
}

const CATEGORY_WORD: Record<string, string> = { cpu: 'CPU', memory: '메모리', ssd: 'SSD', hdd: 'HDD', raid_controller: '컨트롤러', power_supply: 'PSU', network_adapter: 'NIC', gpu: 'GPU', rail_kit: '레일킷', drive_tray: '케이지 킷', riser: '라이저' };
const categoryWord = (category: string): string => CATEGORY_WORD[category] ?? category;

// ─── 진행 띠 ───

/** 지금 어디까지 왔나 — 상태에서 읽는다. 사진(읽음 이상) → 확인(① SKU 또는 모델명이 정해짐) → 채우기(③ 에 든 것) → 저장 */
export function doneSteps(s: ServerSection): Set<RegisterStep> {
  const done = new Set<RegisterStep>();
  if (s.photo.status === 'read' || s.photo.status === 'confirmed') done.add('photo');
  if (s.catalog.model !== null && s.catalogStep !== 'model') done.add('confirm');
  if (s.configCount.installed + s.configCount.included > 0 || s.catalog.productId !== null) done.add('fill');
  if (s.saved !== null) done.add('save');
  return done;
}

const STEP_WORD: Record<RegisterStep, string> = { photo: '사진', confirm: '확인', fill: '채우기', save: '저장' };

export function renderProgress(s: ServerSection): string {
  const done = doneSteps(s);
  const current = REGISTER_STEPS.find((step) => !done.has(step)) ?? null;
  return `<ol class="ic-progress">${REGISTER_STEPS.map((step, n) => `<li class="${done.has(step) ? 'done' : step === current ? 'current' : ''}"><span class="ic-progress-n">${done.has(step) ? '✓' : n + 1}</span>${STEP_WORD[step]}</li>`).join('')}</ol>`;
}

// ─── ④ 점검 후보 ───

const confirmCandidate = (key: string, label: string, scope: InspectCandidate['scope'], hint = ''): InspectCandidate => ({ key, label, type: 'confirm', scope, options: [], hint });
const chooseCandidate  = (key: string, label: string, scope: InspectCandidate['scope'], options: string[], hint = ''): InspectCandidate => ({ key, label, type: 'choose', scope, options, hint });
const textCandidate    = (key: string, label: string, scope: InspectCandidate['scope'], hint = ''): InspectCandidate => ({ key, label, type: 'text', scope, options: [], hint });

/** 후보 전부 — 제품팀 답 전의 목업. 보는 건 남기고 안 보는 건 지운다. scope 는 "어디에 저장할까" 의 재료(unit 열 · pending_number / part_setup_note) */
export const INSPECT_CANDIDATES: InspectCandidateGroup[] = [
  { title: '전원', candidates: [
    chooseCandidate('power_cord',   '전원 코드 규격', 'unit', ['C13', 'C19', '한국 220V 플러그', '없음'], '동봉된 코드'),
    chooseCandidate('input_volt',   '입력 전압',     'pn',   ['110V', '220V', '자동(100~240)'], 'PSU 사양'),
    confirmCandidate('psu_redund',  'PSU 수 · 이중화 확인', 'unit', '두 개 다 켜지나'),
    confirmCandidate('power_cap',   '전력 캡 설정', 'unit', 'iLO Power Settings'),
  ] },
  { title: '관리(iLO)', candidates: [
    confirmCandidate('ilo_login',   'iLO 접속 확인', 'unit'),
    confirmCandidate('ilo_reset',   'iLO 비밀번호 초기화', 'unit', '출고 기본값으로'),
    chooseCandidate('ilo_license',  'iLO 라이선스', 'unit', ['Standard', 'Advanced', '키 등록함'], 'Advanced 는 키 별도'),
    confirmCandidate('mgmt_link',   '관리 포트 링크', 'unit'),
    textCandidate('mac',            'MAC 기록', 'unit', 'iLO · NIC1'),
  ] },
  { title: '펌웨어', candidates: [
    textCandidate('bios_ver',       'BIOS/ROM 판', 'unit'),
    textCandidate('ilo_ver',        'iLO 펌웨어 판', 'unit'),
    textCandidate('ctrl_ver',       '컨트롤러 펌웨어', 'unit'),
    textCandidate('spp_date',       'SPP 적용일', 'unit'),
    confirmCandidate('fw_latest',   '최신 여부 확인', 'unit', 'HPE 지원 사이트 대조'),
  ] },
  { title: '저장 · RAID', candidates: [
    chooseCandidate('raid',         'RAID 구성', 'unit', ['없음', 'RAID 0', 'RAID 1', 'RAID 5', 'RAID 10']),
    textCandidate('disk_seen',      '디스크 인식 수', 'unit', '컨트롤러가 본 수 = ③ 의 수'),
    confirmCandidate('cache_batt',  '캐시 배터리 · 슈퍼캡', 'unit', '컨트롤러에 달려 있나 · 충전됐나'),
    confirmCandidate('wiped',       '데이터 삭제(보안 지움) 완료', 'unit', '중고'),
  ] },
  { title: '기구', candidates: [
    confirmCandidate('rail_kit',    '레일킷', 'unit'),
    confirmCandidate('cma',         'CMA(케이블 암)', 'unit'),
    confirmCandidate('bezel',       '베젤', 'unit'),
    confirmCandidate('blanks',      '블랭크 커버', 'unit', '빈 베이 · 빈 슬롯'),
    chooseCandidate('heatsink',     '히트싱크 종류', 'pn', ['표준', '고성능'], 'CPU TDP 에 맞나'),
    chooseCandidate('fans',         '팬 수', 'pn', ['기본', '풀 팬(High Performance)']),
    confirmCandidate('riser',       '라이저', 'unit'),
    confirmCandidate('screws',      '나사 · 핀', 'unit'),
  ] },
  { title: '외관 · 동작', candidates: [
    confirmCandidate('scratch',     '외관 흠집', 'unit'),
    confirmCandidate('labels',      '라벨 상태', 'unit', '풀아웃 태그 · iLO 라벨'),
    confirmCandidate('seal',        '봉인 스티커', 'unit'),
    confirmCandidate('clean',       '청소', 'unit'),
    confirmCandidate('post_ok',     '부팅 POST 오류 없음', 'unit'),
    confirmCandidate('noise',       '소음 · 팬 이상음', 'unit'),
    confirmCandidate('temp',        '온도 센서 정상', 'unit'),
  ] },
  { title: '식별 · 서류', candidates: [
    confirmCandidate('serial_match', '시리얼 대조', 'unit', '② 와 실물 라벨'),
    confirmCandidate('region',      '리전 꼬리(-B21)', 'pn', '일본 -291 · 중국 -AA1 섞이지 않았나'),
    chooseCandidate('warranty',     '보증 상태', 'unit', ['유효', '만료', '모름'], 'HPE 등록 확인'),
    confirmCandidate('asset_tag',   '자산 태그 부착', 'unit'),
    confirmCandidate('photos',      '사진 보관', 'unit', '앞 · 뒤 · 라벨'),
  ] },
  { title: '소프트웨어', candidates: [
    chooseCandidate('os',           'OS 설치', 'unit', ['없음', 'Windows', 'Linux', 'ESXi']),
    textCandidate('os_key',         'OS 라이선스 키', 'unit'),
    confirmCandidate('acct_reset',  '계정 · 비밀번호 초기화', 'unit'),
  ] },
];

const EMPTY_ANSWER: InspectAnswer = { done: false, choice: '', text: '' };
const SCOPE_CHIP: Record<InspectCandidate['scope'], string> = { unit: '<span class="chip ic-scope-unit" title="한 대마다 다르다 — unit 에 적힌다">한 대</span>', pn: '<span class="chip ic-scope-pn" title="같은 PN 이면 늘 같다 — 세팅 메모(PN) 몫">PN</span>' };

/** 후보의 값 칸 — 체크 · 고르기 · 적기 */
function inspectField(c: InspectCandidate, a: InspectAnswer): string {
  if (c.type === 'confirm') return `<label class="ic-inspect-confirm"><input type="checkbox" data-action="inspect" data-key="${c.key}" ${a.done ? 'checked' : ''}> 확인</label>`;
  if (c.type === 'choose') return `<select data-action="inspect" data-key="${c.key}"><option value="">고르기</option>${c.options.map((o) => `<option ${a.choice === o ? 'selected' : ''}>${escapeHtml(o)}</option>`).join('')}</select>`;
  return `<input type="text" data-action="inspect" data-key="${c.key}" value="${escapeHtml(a.text)}" placeholder="적기">`;
}

/** 답한 후보 수 — 체크됨 · 골랐음 · 적었음 */
export function inspectDoneCount(s: ServerSection): number {
  return Object.values(s.inspect).filter((a) => a.done || a.choice !== '' || a.text !== '').length;
}
export const INSPECT_TOTAL = INSPECT_CANDIDATES.reduce((n, g) => n + g.candidates.length, 0);

/** ④ 점검 후보 — 묶음마다 표. 한 줄 = 라벨 · 값 칸 · 한 대/PN 칩 · 힌트 */
export function renderInspect(s: ServerSection): string {
  const group = (g: InspectCandidateGroup): string => `
    <div class="ic-inspect-group">
      <h4>${g.title}</h4>
      ${g.candidates.map((c) => `<div class="ic-inspect-row"><span class="ic-inspect-label">${escapeHtml(c.label)}${c.hint === '' ? '' : `<small class="ic-inspect-hint text-muted">${escapeHtml(c.hint)}</small>`}</span><span class="ic-inspect-field">${inspectField(c, s.inspect[c.key] ?? EMPTY_ANSWER)}</span>${SCOPE_CHIP[c.scope]}</div>`).join('')}
    </div>`;
  return `
    <section class="form-card ic-card ic-inspect">
      <h2>④ 점검 <small>이 한 대에서 보는 것 — 부품이 아니라 확인 · 고르기 · 적기. <b>후보 ${INSPECT_TOTAL}</b>, 제품팀이 남기고 지웁니다 · 답한 것 <span class="ic-inspect-count">${inspectDoneCount(s)}</span></small></h2>
      <div class="ic-inspect-groups">${INSPECT_CANDIDATES.map(group).join('')}</div>
    </section>`;
}

// ─── 저장 결과 ───

/** 저장이 만든 것 — 번호 · 구성 · 자식 수. 다음 할 일 둘 */
export function renderSaved(r: SaveResult): string {
  return `
    <section class="form-card ic-card ic-saved">
      <h2>✓ 등록됐습니다</h2>
      <div class="ic-brief">
        ${brief('서버 unit', `<span class="mono">#${r.unit_id}</span>`)}
        ${brief('구성', `<span class="mono">${escapeHtml(r.hclt_number)}</span>${r.is_new_product ? ' <span class="chip">새 구성</span>' : ' <span class="chip chip-success">기존 구성</span>'}`)}
        ${brief('안에 든 unit', `장착 ${r.installed_count} · 기본 포함(자리 없음) ${r.included_count}`)}
        ${brief('점검', `${r.inspect_done} / ${r.inspect_total}`)}
        ${brief('상태', '검수 중(inspecting) — 재고 목록에서 검수 완료로')}
      </div>
      <div class="ic-saved-actions">
        <a class="btn-primary" href="/inventory">재고 목록에서 보기</a>
        <button type="button" class="btn-ghost" data-action="next-unit">같은 구성으로 한 대 더</button>
      </div>
    </section>`;
}

// ─── ① 카탈로그 ───

/** ① 카탈로그 — 자동완성 입력 + 접히는 계단 + 상세. 입력 줄은 고정, 제안 목록(`.ic-suggest`)과 계단(`.ic-catalog-found`)만 다시 그린다(입력 포커스를 안 잃게) */
export function renderCatalog(s: ServerSection): string {
  return `
    <section class="form-card ic-card sr-catalog">
      <h2>① 카탈로그 <small>어떤 서버인가 — 모델명 · SKU · 이미 등록된 같은 구성</small></h2>
      <div class="ic-search">
        <span class="form-label">모델명 · PN</span>
        <div class="ic-suggest-wrap">
          <input class="input" data-action="catalog-query" value="${escapeHtml(s.catalog.query)}" placeholder="DL380 Gen10 또는 868703-B21" autocomplete="off" />
          <div class="ic-suggest">${renderSuggest(s)}</div>
        </div>
        ${photoMark(s, 'catalog.query')}
      </div>
      <div class="ic-catalog-found">${renderCatalogFound(s)}</div>
    </section>`;
}

/** 편 수 칩 — 모델 행 하나에 편(퀵스펙) 여럿이 달릴 때만(2 이상). 하나면 말할 게 없다(2026-10-06) */
const docCountChip = (docCount: number): string => docCount >= 2 ? ` <span class="chip ic-doc-count" title="이 모델에 실린 퀵스펙 편 수 — 본편 · 부록 · 옛 편">편 ${docCount}</span>` : '';

/** 제안 목록 — 치는 동안만 뜬다(모델명을 고르면 닫힘). 종류 라벨(SKU · 모델명)을 다른 꼴로, 맞은 글자는 굵게(Baymard) */
export function renderSuggest(s: ServerSection): string {
  const { catalog, catalogOptions } = s;
  const q = catalog.query.trim();
  if (q === '' || catalog.model !== null) return '';
  if (catalogOptions.matches.length === 0) return '<div class="ic-suggest-empty text-muted">맞는 모델명 · SKU 가 없습니다 — 카탈로그에 없는 모델은 등록 요청으로(다음)</div>';
  const item = (m: CatalogMatch, index: number): string => m.kind === 'sku'
    ? `<button type="button" class="ic-suggest-item" data-action="match" data-index="${index}"><span class="chip">CTO</span><b>${markMatch(m.sku.option_part, q)}</b> <span>${escapeHtml(m.sku.description ?? '')}</span> <span class="text-muted">${escapeHtml(m.model.model ?? '')}</span></button>`
    : m.kind === 'bto'
    ? `<button type="button" class="ic-suggest-item" data-action="match" data-index="${index}"><span class="chip chip-success">BTO</span><b>${markMatch(m.bto.option_part, q)}</b> <span>${escapeHtml(m.bto.description ?? '')}</span> <span class="text-muted">${escapeHtml(m.model.model ?? '')}</span></button>`
    : `<button type="button" class="ic-suggest-item" data-action="match" data-index="${index}"><span class="chip chip-primary">모델명</span><b>${markMatch(m.model.model ?? '(모델명 없음)', q)}</b> <span>${markMatch(m.model.title, q)}</span>${docCountChip(m.model.doc_count)} <span class="text-muted">${m.model.vendor}</span></button>`;
  return catalogOptions.matches.map(item).join('');
}

/** 맞은 글자를 굵게 — 대소문자 무시, 첫 자리만 */
function markMatch(label: string, q: string): string {
  const at = label.toLowerCase().indexOf(q.toLowerCase());
  if (at < 0) return escapeHtml(label);
  return `${escapeHtml(label.slice(0, at))}<mark>${escapeHtml(label.slice(at, at + q.length))}</mark>${escapeHtml(label.slice(at + q.length))}`;
}

/** ① 의 계단 — 모델명 → SKU → 등록된 구성. 모델명은 제안 목록에서 고른다. 펼쳐진 단(catalogStep)은 하나, 고른 단은 한 줄 + 바꾸기. 고른 모델명 · SKU 아래엔 상세 전부 */
export function renderCatalogFound(s: ServerSection): string {
  const { catalog, catalogStep, catalogOptions } = s;
  if (catalog.model === null) return '';
  const modelRow = renderModelChosen(s) + (catalogOptions.modelDetail === null ? loading('모델 상세') : renderModelDetail(catalogOptions.modelDetail));
  const skuRow = catalogStep === 'sku' ? renderSkuChoices(s) : renderSkuChosen(s) + renderSkuDetailOrEmpty(s);
  const bSkuDecided = catalogStep === 'product' || catalogStep === null;
  const productRow = !bSkuDecided ? '' : catalogStep === 'product' ? renderProductChoices(s) : renderProductChosen(s);
  return modelRow + skuRow + productRow;
}

const loading = (what: string): string => `<div class="ic-kv text-muted">${what} 받는 중…</div>`;
const changeButton = (step: CatalogStep): string => `<button type="button" class="btn-ghost ic-change" data-action="catalog-reopen" data-step="${step}">바꾸기</button>`;
const chosenRow = (label: string, value: string, step: CatalogStep, mark: string): string =>
  `<div class="kv-row ic-chosen"><span class="kv-key">${label}</span><span class="kv-val">${mark}${value} ${changeButton(step)}</span></div>`;

function renderModelChosen(s: ServerSection): string {
  const m = s.catalog.model;
  assert(m !== null, '모델명이 없는데 접힌 모델명 줄을 그리려 한다');
  return chosenRow('모델명', `<b>${escapeHtml(m.model ?? '(모델명 없음)')}</b> · ${escapeHtml(m.title)}${docCountChip(m.doc_count)} · <span class="text-muted">${m.vendor}</span>`, 'model', photoMark(s, 'catalog.query'));
}

/** 요약 칸 하나 — 라벨 위 · 값 아래. 늘 보이는 줄의 재료 */
const brief = (label: string, value: string): string => `<span class="ic-brief-item"><span class="ic-brief-label">${label}</span><span class="ic-brief-value">${value}</span></span>`;

/** 늘 보이는 줄 — 섀시를 고를 때 보는 값만(현민 2026-10-02: CTO 는 섀시 값만. CPU 는 ③ 구성). 빈 값은 — 로 자리만 */
function renderBrief(d: ChassisModelDetail | ChassisDetail): string {
  return `<div class="ic-brief">
    ${brief('폼팩터', text(d.form_factor))}
    ${brief('랙 높이', text(d.rack_unit, 'U'))}
    ${brief('CPU 소켓', text(d.max_cpu_sockets))}
    ${brief('메모리', `${text(d.memory_type)} · DIMM ${text(d.memory_dimm_slots)} · 최대 ${text(d.memory_max_capacity_gb, ' GB')}`)}
    ${brief('PSU', `${text(d.psu_slot_count)} · ${text(d.psu_max_watt, ' W')}`)}
    ${brief('확장', `PCIe ${text(d.pcie_generation)} · 슬롯 ${text(d.pcie_total_slots)} · OCP ${text(d.ocp_slot_count)}`)}
    ${brief('BMC', text(d.bmc_version))}
  </div>`;
}

/** 모델 상세 — 늘 보이는 줄 + ▸ 전체 보기(chassis_model 열 전부 묶음 다섯 + 퀵스펙 원문, 절마다 또 접힘) */
function renderModelDetail(d: ChassisModelDetail): string {
  const base = kvGroup('기본',
    kv('제조사', text(d.vendor)) + kv('세대', text(d.generation)) + kv('폼팩터', text(d.form_factor)) + kv('랙 높이', text(d.rack_unit, 'U')) +
    kv('원격 관리(BMC)', text(d.bmc_version)) + kv('퀵스펙', renderModelDocs(d.docs)));
  return `<div class="ic-kv ic-detail">
    <h3>모델 상세 <small class="text-muted">카탈로그 chassis_model — 이 모델의 모든 대가 같은 값</small></h3>
    ${renderBrief(d)}
    <details class="ic-all"><summary>전체 보기</summary>${base}${renderSpecGroups(d)}${renderBulletinSpecs(d.specs)}</details>
  </div>`;
}

/** 편 목록 — 본편 · 부록 · 옛 편 한 줄씩(서버 순서 그대로). 역할 칩 + 번호 + 제목 */
const MODEL_ROLE_LABELS: Record<ModelDocView['model_role'], string> = { main: '본편', supplement: '부록', edition: '옛 편' };
function renderModelDocs(docs: ModelDocView[]): string {
  if (docs.length === 0) return '<span class="text-muted">아직 실리지 않음</span>';
  return docs.map((doc) => `<div class="ic-doc-line"><span class="chip">${MODEL_ROLE_LABELS[doc.model_role]}</span> ${doc.doc_no} · ${escapeHtml(doc.title ?? '(제목 없음)')}</div>`).join('');
}

/** 모델 · SKU 가 같이 갖는 제원 열 — 묶음 넷 */
function renderSpecGroups(d: ChassisModelDetail | ChassisDetail): string {
  return kvGroup('CPU · 메모리',
      kv('CPU 제조사', text(d.cpu_vendor)) + kv('CPU 소켓 수', text(d.max_cpu_sockets)) + kv('칩셋', text(d.chipset)) +
      kv('메모리 종류', text(d.memory_type)) + kv('DIMM 슬롯 수', text(d.memory_dimm_slots)) + kv('소켓당 DIMM', text(d.dimm_per_socket)) +
      kv('메모리 최대', text(d.memory_max_capacity_gb, ' GB')) + kv('메모리 보호', text(d.memory_protection)))
    + kvGroup('확장',
      kv('PCIe 세대', text(d.pcie_generation)) + kv('확장 슬롯 수', text(d.pcie_total_slots)) + kv('OCP 슬롯', text(d.ocp_slot_count)) +
      kv('GPU 최대(싱글 와이드)', text(d.max_gpu_single_wide)) + kv('GPU 최대(더블 와이드)', text(d.max_gpu_double_wide)))
    + kvGroup('전원 · 네트워크',
      kv('PSU 슬롯', text(d.psu_slot_count)) + kv('PSU 이중화', text(d.psu_redundant)) + kv('PSU 최소', text(d.psu_min_watt, ' W')) + kv('PSU 최대', text(d.psu_max_watt, ' W')) +
      kv('내장 NIC', `${text(d.embedded_nic_ports)} 포트 · ${text(d.embedded_nic_speed)}`))
    + kvGroup('환경',
      kv('동작 온도 최대', text(d.operating_temp_max_c, ' ℃')) + kv('TPM', text(d.has_tpm)));
}

/** 퀵스펙 줄 — 절(section)마다 접어 둔다(한 편 65줄 · 긴 문단이라 펼쳐 두면 ② ③ 이 화면 밖으로 밀린다). 문서 순서 그대로 */
function renderBulletinSpecs(specs: BulletinSpec[]): string {
  if (specs.length === 0) return kvGroup('퀵스펙 원문', `<div class="text-muted">아직 실리지 않음 — 로더가 불레틴을 실으면 여기 뜹니다</div>`);
  const sections: string[] = [];
  for (const spec of specs) if (!sections.includes(spec.section)) sections.push(spec.section);
  const section = (name: string): string => {
    const rows = specs.filter((x) => x.section === name);
    return `<details class="ic-spec-section"><summary>퀵스펙 · ${escapeHtml(name)} <span class="chip">${rows.length}</span></summary>${rows.map((x) => kv(escapeHtml(x.label), escapeHtml(x.value))).join('')}</details>`;
  };
  return `<div class="ic-kv-group"><h4>퀵스펙 원문</h4>${sections.map(section).join('')}</div>`;
}

/** SKU 단 — CTO 칩 줄과 BTO 칩 줄. 차이가 여기서 보인다: CTO 는 빈 섀시, BTO 는 CPU · 메모리 · 컨트롤러 · PSU 가 든 채(2026-10-05) */
function renderSkuChoices(s: ServerSection): string {
  const { catalog, catalogOptions } = s;
  const chip = (value: string, label: string, title: string, active: boolean): string =>
    `<label class="chip ic-chip${active ? ' chip-primary' : ''}" title="${escapeHtml(title)}"><input type="radio" name="sku" data-action="sku" value="${escapeHtml(value)}" ${active ? 'checked' : ''}/>${escapeHtml(label)}</label>`;
  const ctoChips = catalogOptions.skus.map((k) => chip(`cto:${k.option_part}`, k.option_part, k.description ?? '', catalog.sku !== null && catalog.sku.option_part === k.option_part)).join('');
  const btoChips = catalogOptions.preconfigs.map((b) => chip(`bto:${b.id}`, `${b.option_part} · ${btoShort(b)}`, b.description ?? '', catalog.bto !== null && catalog.bto.id === b.id)).join('');
  const unknown = chip('', 'SKU 모름 — 모델 값만', '', false);   // 고르는 중엔 미리 켜지 않는다 — null 은 "아직 안 골랐다" 와 같아서
  return `
    <div class="kv-row"><span class="kv-key">CTO 섀시 <small class="text-muted">빈 섀시 — CPU · 메모리 · 디스크 · PSU 는 ③ 에서</small></span><span class="kv-val ic-chips">${ctoChips || '<span class="text-muted">없음</span>'}${unknown}</span></div>
    <div class="kv-row"><span class="kv-key">BTO <small class="text-muted">기본 포함이 든 채 오는 모델</small></span><span class="kv-val ic-chips">${btoChips || '<span class="text-muted">이 모델명엔 BTO 가 없습니다</span>'}</span></div>`;
}

/** BTO 한 줄의 짧은 말 — '4208 ×1 · 32GB · P408i-a · 500W'. 열이 비면 설명에서 */
function btoShort(b: PreconfigView): string {
  const parts = btoIncluded(b).map((x) => x.label);
  return parts.length > 0 ? parts.join(' · ') : (b.description ?? '').replace(/^HPE ProLiant \S+ \S+ /, '').replace(/ Server$/, '');
}

/** BTO 의 기본 포함 — 행의 구성 열(cpu_part · cpu_count · memory · storage_controller · psu)에서. 열이 비면 설명 글에서 읽는다(시연용 — Load 가 BTO attrs 를 실으면 열만 쓴다) */
export function btoIncluded(b: PreconfigView): { category: string; label: string; option_part: string; description: string; qty: number }[] {
  const d = b.description ?? '';
  const cpu = d.match(/\b(\d{4}[A-Z]?)\s+[\d.]+GHz\s+(\d+)-core\s+(\d)P\b/);
  const mem = d.match(/\b(\d+)GB-R\b/);
  const ctl = d.match(/\b(P\d{3}i-[ap]|S100i|E208i-[ap]|MR\d{3}i-[ap]|P\d{3}i-p)\b/);
  const psu = d.match(/\b(\d{3,4})W PS\b/);
  const out: { category: string; label: string; option_part: string; description: string; qty: number }[] = [];
  const cpuQty = b.cpu_count ?? (cpu ? Number(cpu[3]) : 0);
  if (cpuQty > 0) out.push({ category: 'cpu', label: `${cpu ? cpu[1] : 'CPU'} ×${cpuQty}`, option_part: b.cpu_part ?? `BTO-CPU-${cpu ? cpu[1] : '?'}`, description: cpu ? `Intel Xeon ${cpu[1]} (${cpu[2]}-core) — BTO 기본` : 'BTO 기본 CPU', qty: cpuQty });
  const memGb = b.memory_capacity_gb ?? (mem ? Number(mem[1]) : 0);
  const memQty = b.memory_dimm_count ?? (memGb > 0 ? 1 : 0);
  if (memGb > 0) out.push({ category: 'memory', label: `${memGb}GB ×${memQty}`, option_part: `BTO-MEM-${memGb}GB`, description: `HPE ${memGb}GB RDIMM — BTO 기본`, qty: memQty });
  const ctlName = ctl ? ctl[1] : (b.storage_controller ?? '');
  if (ctlName !== '' && ctlName !== 'S100i') out.push({ category: 'raid_controller', label: ctlName, option_part: `BTO-CTL-${ctlName}`, description: `HPE Smart Array ${ctlName} — BTO 기본`, qty: 1 });
  const psuQty = b.psu_count ?? (psu ? 1 : 0);
  if (psuQty > 0) out.push({ category: 'power_supply', label: `${psu ? psu[1] + 'W' : 'PSU'} ×${psuQty}`, option_part: b.psu_part ?? `BTO-PSU-${psu ? psu[1] : '?'}`, description: `HPE ${psu ? psu[1] + 'W' : ''} Power Supply — BTO 기본`, qty: psuQty });
  return out;
}

function renderSkuChosen(s: ServerSection): string {
  const { sku, bto } = s.catalog;
  if (bto !== null) {
    const included = btoIncluded(bto).map((x) => x.label).join(' · ');
    return chosenRow('SKU', `<span class="chip chip-success">BTO</span> <b>${escapeHtml(bto.option_part)}</b> · ${escapeHtml(bto.description ?? '')}`, 'sku', '')
      + `<div class="kv-row ic-included-row"><span class="kv-key">기본 포함</span><span class="kv-val">${included === '' ? '<span class="text-muted">설명에서 못 읽음</span>' : escapeHtml(included)} <span class="text-muted">— ③ 에 「기본 포함」 칩으로 미리 앉습니다</span></span></div>`;
  }
  if (sku === null) return chosenRow('SKU', 'SKU 모름 — 모델 값만', 'sku', '');
  return chosenRow('SKU', `<span class="chip">CTO</span> <b>${escapeHtml(sku.option_part)}</b> · ${escapeHtml(sku.description ?? '')}`, 'sku', photoMark(s, 'catalog.sku'))
    + `<div class="kv-row ic-included-row"><span class="kv-key">기본 포함</span><span class="kv-val text-muted">없음 — 빈 섀시. CPU · 메모리 · 디스크 · PSU 는 ③ 에서 채웁니다</span></div>`;
}

function renderSkuDetailOrEmpty(s: ServerSection): string {
  if (s.catalog.sku === null && s.catalog.bto === null) return '';
  const d = s.catalogOptions.skuDetail;
  if (d === null && s.catalog.bto !== null && s.catalog.bto.base_chassis_id === null) return `<div class="ic-kv text-muted">바탕 CTO 섀시가 카탈로그에 안 걸려 있어 SKU 상세가 없습니다</div>`;
  return d === null ? loading('SKU 상세') : renderSkuDetail(d);
}

/** 베이 한 자리를 한 토막으로 — 'Front 8 SFF 기본'. 빈 칸은 뺀다 */
function bayText(b: ChassisBayView): string {
  const parts = [b.bay_position, b.bay_count === null ? null : String(b.bay_count), b.bay_type, b.is_default === true ? '기본' : null]
    .filter((x): x is string => x !== null && x !== '');
  return escapeHtml(parts.join(' '));
}

/** SKU 상세 — 늘 보이는 줄(파트넘버 · 설명 · 베이) + ▸ 전체 보기(chassis 열 전부 + 기본 사양 + 베이) */
function renderSkuDetail(d: ChassisDetail): string {
  const skuBrief = `<div class="ic-brief">
    ${brief('설명', text(d.description))}
    ${brief('베이', d.bays.length === 0 ? EMPTY : d.bays.map(bayText).join(' · '))}
    ${brief('드라이브 베이 최대', text(d.max_drive_bays_total))}
    ${brief('스페어 PN', text(d.spare_part))}
    ${brief('어셈블리 PN', text(d.assembly_part))}
    ${brief('공장 PN(FIO)', text(d.factory_part))}
  </div>`;
  const numbers = kvGroup('파트넘버 · 설명',
    kv('옵션 PN', text(d.option_part)) + kv('스페어 PN', text(d.spare_part)) + kv('어셈블리 PN', text(d.assembly_part)) + kv('공장 PN(FIO)', text(d.factory_part)) +
    kv('SKU 종류', text(d.sku_type)) + kv('설명', text(d.description)) + kv('원문 설명', text(d.description_raw)));
  const physical = kvGroup('크기 · 무게',
    kv('높이 · 너비 · 깊이', `${text(d.height_cm, ' cm')} · ${text(d.width_cm, ' cm')} · ${text(d.depth_cm, ' cm')}`) +
    kv('무게', `${text(d.weight_min_kg, ' kg')} ~ ${text(d.weight_max_kg, ' kg')}`) + kv('드라이브 베이 최대', text(d.max_drive_bays_total)) +
    kv('퀵스펙 판', text(d.quickspec_version)) + kv('메모', text(d.notes)));
  return `<div class="ic-kv ic-detail">
    <h3>SKU 상세 <small class="text-muted">카탈로그 chassis — 이 PN 의 값</small></h3>
    ${skuBrief}
    <details class="ic-all"><summary>전체 보기</summary>${numbers}${renderChassisSpecs(d.specs)}${renderBays(d.bays)}${renderBrief(d)}${renderSpecGroups(d)}${physical}</details>
  </div>`;
}

/** 기본 사양 — 퀵스펙 Standard Features 의 라벨 · 값 그대로(USB · Fans · PCIe · Power Supply …). 안 실린 SKU 는 한 줄 안내 */
function renderChassisSpecs(specs: ChassisSpec[]): string {
  if (specs.length === 0) return kvGroup('기본 사양', `<div class="text-muted">아직 실리지 않음 — 불레틴이 이 SKU 를 실으면 여기 뜹니다</div>`);
  return kvGroup('기본 사양', specs.map((x) => kv(escapeHtml(x.label), escapeHtml(x.value))).join(''));
}

/** 베이 — 자리마다 한 줄. 위치 → 수 · 종류 · 기본 여부 · 원문 */
function renderBays(bays: ChassisBayView[]): string {
  if (bays.length === 0) return kvGroup('베이', `<div class="text-muted">베이 정보 없음</div>`);
  return kvGroup('베이', bays.map((b) => kv(text(b.bay_position), `${text(b.bay_count)} · ${text(b.bay_type)} · ${b.is_default === true ? '기본' : '옵션'} · <span class="text-muted">${text(b.bay_raw)}</span>`)).join(''));
}

function renderProductChoices(s: ServerSection): string {
  const { catalog, catalogOptions } = s;
  const product = (p: HcltProductView): string => {
    const active = catalog.productId === p.id;
    const match = s.photo.status === 'confirmed' ? ' <span class="chip chip-success">읽은 부품과 일치 ✓</span>' : '';
    return `<label class="ic-choice${active ? ' active' : ''}"><input type="radio" name="product" data-action="product" value="${escapeHtml(p.id)}" ${active ? 'checked' : ''}/> <b>${escapeHtml(p.hclt_number)}</b> ${escapeHtml(p.description ?? '')} <span class="chip">재고 ${p.unit_count}</span>${match}</label>`;
  };
  const fresh = `<label class="ic-choice"><input type="radio" name="product" data-action="product" value="" /> 새 구성 — 아래 ③에서 채움</label>`;   // 미리 안 켠다 — null 은 "아직 안 골랐다" 와 같아서
  return `<div class="kv-row"><span class="kv-key">등록된 구성 <small class="text-muted">같은 구성의 서버가 이미 있나</small></span><span class="kv-val ic-choices">${catalogOptions.products.map(product).join('')}${fresh}</span></div>`;
}

function renderProductChosen(s: ServerSection): string {
  const id = s.catalog.productId;
  if (id === null) return chosenRow('등록된 구성', '새 구성 — 아래 ③에서 채움', 'product', '');
  const p = s.catalogOptions.products.find((x) => x.id === id);
  assert(p !== undefined, `고른 구성 ${id} 가 이 모델명의 목록에 없다`);
  return chosenRow('등록된 구성', `<b>${escapeHtml(p.hclt_number)}</b> · ${escapeHtml(p.description ?? '')} · <span class="chip">재고 ${p.unit_count}</span>`, 'product', '');
}

// ─── ② 실물 ───

/** ② 실물 — 번호 세 층 + 컨디션 · 창고 · 구역 · 원가 · 출처. 사이트 공통 form-grid(3열) */
export function renderUnit(s: ServerSection): string {
  const u = s.unit;
  const field = (key: keyof typeof u, label: string, inner: string, extraClass = ''): string =>
    `<label class="form-field ${extraClass}"><span class="form-label">${label} ${photoMark(s, `unit.${key}`)}</span>${inner}</label>`;
  const option = (value: string, label: string, current: string): string => `<option value="${escapeHtml(value)}" ${current === value ? 'selected' : ''}>${escapeHtml(label)}</option>`;
  return `
    <section class="form-card ic-card sr-unit">
      <h2>② 실물 <small>이 한 대에만 붙는 값 — 시리얼 · 컨디션 · 어디에 두나 · 얼마에 샀나</small></h2>
      <div class="form-grid">
        ${field('serial_number', '시리얼', `<input type="text" data-action="unit" data-field="serial_number" value="${escapeHtml(u.serial_number)}" placeholder="바코드 찍기" />`)}
        ${field('condition', '컨디션', `<select data-action="unit" data-field="condition">${['', 'F/S', 'NOB', 'Newbulk', 'Newpull', 'Used', 'REF'].map((c) => option(c, c || '고르기', u.condition)).join('')}</select>`)}
        ${field('origin', '출처', `<select data-action="unit" data-field="origin">${option('found', '실사 발견 · 등록', u.origin)}${option('legacy', '옛 재고(이카운트)', u.origin)}</select>`)}
        ${field('warehouse', '창고', `<select data-action="unit" data-field="warehouse">${option('본사', '본사', u.warehouse)}</select>`)}
        ${field('zone', '구역', `<select data-action="unit" data-field="zone">${['', '랙1', '랙2', 'A-1 메모리', 'A-2 CPU', 'B-1 드라이브'].map((z) => option(z, z || '카테고리 기본 자리', u.zone)).join('')}</select>`)}
        ${field('cost_amount', '원가', `<span class="ic-money"><input type="text" data-action="unit" data-field="cost_amount" value="${escapeHtml(u.cost_amount)}" placeholder="0" /><select data-action="unit" data-field="cost_currency">${option('KRW', 'KRW', u.cost_currency)}${option('USD', 'USD', u.cost_currency)}</select></span>`)}
        ${field('security_id', 'iLO Security ID', `<input type="text" data-action="unit" data-field="security_id" value="${escapeHtml(u.security_id)}" placeholder="HPE 표 — 풀아웃 태그" />`)}
        ${field('tracking_number', 'CT 번호', `<input type="text" data-action="unit" data-field="tracking_number" value="${escapeHtml(u.tracking_number)}" placeholder="HPE 표" />`)}
        ${field('manufacture_date', '제조일', `<input type="text" data-action="unit" data-field="manufacture_date" value="${escapeHtml(u.manufacture_date)}" placeholder="2020-05-19" />`)}
      </div>
      <p class="form-hint">📷 는 사진에서 읽은 값 — 고치면 표시가 사라집니다.</p>
    </section>`;
}
